import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportTable } from '@/components/ReportTable';
import { apiFetch } from '@/lib/api';
import { formatDate } from '@/lib/utils';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ApAgingRow {
  supplier_id: number;
  supplier_name: string;
  b0_30: number;
  b31_60: number;
  b61_90: number;
  b90_plus: number;
  total: number;
}

interface ApAgingTotals {
  b0_30: number;
  b31_60: number;
  b61_90: number;
  b90_plus: number;
  total: number;
}

interface ApAgingData {
  as_of: string;
  rows: ApAgingRow[];
  totals: ApAgingTotals;
}

interface ApAgingResponse {
  data: ApAgingData;
}

// ─── Columns ───────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'supplier_name', header: 'Supplier' },
  { key: 'b0_30', header: '0–30', money: true },
  { key: 'b31_60', header: '31–60', money: true },
  { key: 'b61_90', header: '61–90', money: true },
  { key: 'b90_plus', header: '90+', money: true },
  { key: 'total', header: 'Total', money: true },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function ApAgingReport() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report', 'ap-aging'],
    queryFn: () => apiFetch<ApAgingResponse>('/api/reports/ap-aging'),
  });

  const report = data?.data;
  const rows = (report?.rows ?? []) as unknown as Record<string, unknown>[];
  const totalsRow = report ? { supplier_name: 'Total', ...report.totals } : undefined;

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <h1 className="text-2xl font-bold tracking-tight">AP Aging</h1>
        <ExportButtons endpoint="/api/reports/ap-aging" />
      </div>

      {report && (
        <p className="text-sm text-muted-foreground mb-6">As of {formatDate(report.as_of)}</p>
      )}

      {/* Loading / error */}
      {isLoading && (
        <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>
      )}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load report'}
        </p>
      )}

      {report && (
        <ReportTable columns={COLUMNS} rows={rows} totalsRow={totalsRow} keyField="supplier_id" />
      )}
    </div>
  );
}
